<template>
  <v-dialog v-model="modal" width="60%" persistent>
    <v-card>
      <v-card-title class="text-headline-small bg-grey-lighten-2" primary-title>
        <span class="text-headline-small">Générer un rapport – paramètres d'affichage</span>
      </v-card-title>
      <v-card-text>
        <v-container fluid>
          <v-row class="mb-2 mt-2" :gap="[24, 8]">
            <div class="form-label">
              <label>Gabarit</label>
            </div>
            <v-select
              ref="templateSelect"
              :model="template"
              :items="predefinedReports"
              @update:model-value="loadTemplate"
              variant="outlined"
            ></v-select>
          </v-row>
          <v-row class="mb-2" :gap="[24, 8]">
            <v-col cols="12">
              <div class="form-label">
                <label>Type de rapport</label>
              </div>
              <v-radio-group v-model="type" inline class="mt-1" hide-details>
                <v-radio
                  true-icon="mdi-radiobox-marked"
                  false-icon="mdi-radiobox-blank"
                  color="primary"
                  v-for="type in types"
                  :key="type.value"
                  :label="type.text"
                  :value="type.value"
                ></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row :gap="[24, 8]">
            <v-col>
              <div class="form-label">
                <label>Sections à afficher</label>
              </div>
              <v-row :gap="[24, 8]">
                <v-col cols="4">
                  <v-checkbox
                    true-icon="mdi-checkbox-marked"
                    false-icon="mdi-checkbox-blank-outline"
                    v-model="showComponents"
                    label="Afficher les assamblages"
                    class="mt-0"
                    color="primary"
                    @update:model-value="informationRipple()"
                  >
                  </v-checkbox>
                </v-col>
                <v-col cols="4">
                  <v-checkbox
                    true-icon="mdi-checkbox-marked"
                    false-icon="mdi-checkbox-blank-outline"
                    v-model="showMaterials"
                    label="Afficher les matériaux"
                    class="mt-0"
                    color="primary"
                    :disabled="!showComponents"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row class="mb-2" :gap="[24, 8]">
            <v-col cols="12">
              <div class="form-label">
                <label class="d-block">Colonnes sélectionnées</label>
              </div>
              <div v-if="selected_columns.length === 0">Aucune</div>
              <div class="selected-chips">
                <v-chip
                  :closable="!selected_columns.selected"
                  @update:model-value="toggleColumn(selectedColumn)"
                  v-for="selectedColumn in selected_columns"
                  :key="selectedColumn.value"
                  >{{ selectedColumn.text }}</v-chip
                >
              </div>
            </v-col>
          </v-row>
          <v-row class="mb-2 report-options-row" :gap="0">
            <div class="form-label">
              <label>Colonnes à afficher</label>
              <div class="select-all-container">
                <div @click="selectAllColumns()">Toutes</div>
              </div>
              <div class="select-all-container">
                <div @click="resetColumns">Aucune</div>
              </div>
            </div>
            <v-col class="flex-column selected-options" cols="3" v-for="column in columns" :key="column.value">
              <div class="column-name-container">
                <span class="column-name-block">
                  <div
                    class="column-name-text"
                    :class="[column.selected ? 'selected' : '', column.selectedNew ? 'selected-new' : '']"
                    @click="toggleColumn(column)"
                  >
                    {{ column.text }}
                  </div>
                </span>
              </div>
            </v-col>
          </v-row>
          <v-row class="mb-2" :gap="[24, 8]">
            <v-col cols="12">
              <div class="form-label">
                <label class="d-block">Totaux sélectionnées</label>
              </div>
              <div v-if="selected_totals.length === 0">Aucune</div>
              <div class="selected-chips">
                <v-chip
                  :closable="!selected_totals.selected"
                  @update:model-value="toggleTotal(selectedTotal)"
                  v-for="selectedTotal in selected_totals"
                  :key="selectedTotal.value"
                  >{{ selectedTotal.text }}</v-chip
                >
              </div>
            </v-col>
          </v-row>
          <v-row class="mb-2 report-options-row" :gap="0">
            <div class="form-label">
              <label>Totaux à afficher</label>
              <div class="select-all-container">
                <div @click="selectAllTotals()">Tous</div>
              </div>
              <div class="select-all-container">
                <div @click="resetTotals">Aucun</div>
              </div>
            </div>
            <v-col cols="3" class="selected-options" v-for="total in totals" :key="total.value">
              <div class="column-name-container">
                <span class="column-name-block">
                  <div
                    class="column-name-text"
                    :class="[total.selected ? 'selected' : '', total.selectedNew ? 'selected-new' : '']"
                    @click="toggleTotal(total)"
                  >
                    {{ total.text }}
                  </div>
                </span>
              </div>
            </v-col>
          </v-row>
          <v-row :gap="[24, 8]">
            <v-col>
              <div class="form-label">
                <label>Signatures</label>
              </div>
              <v-checkbox
                true-icon="mdi-checkbox-marked"
                false-icon="mdi-checkbox-blank-outline"
                v-model="signature"
                hide-details
                class="mt-0"
                label="Inclure l'encadré des signatures"
                color="primary"
              >
              </v-checkbox>
            </v-col>
            <v-col>
              <div class="form-label">
                <label>Sous-total</label>
              </div>
              <v-checkbox
                true-icon="mdi-checkbox-marked"
                false-icon="mdi-checkbox-blank-outline"
                v-model="sousTotal"
                hide-details
                class="mt-0"
                label="Inclure le sous-total"
                color="primary"
              >
              </v-checkbox>
            </v-col>
            <v-col>
              <div class="form-label">
                <label>Note</label>
              </div>
              <v-checkbox
                true-icon="mdi-checkbox-marked"
                false-icon="mdi-checkbox-blank-outline"
                v-model="note"
                hide-details
                class="mt-0"
                label="Inclure la note"
                color="primary"
              >
              </v-checkbox>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions class="py-3">
        <v-spacer></v-spacer>
        <v-btn @click="cancel" color="primary" variant="elevated"> Annuler </v-btn>
        <v-btn @click="openReports(job)" color="primary" variant="elevated"> Générer </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'reportModal',
  data() {
    return {
      types: [
        { value: 'contractor', text: 'Entrepreneurs' },
        { value: 'client', text: 'Clients' },
      ],
      type: 'contractor',
      only: null,
      showComponents: true,
      showMaterials: true,
      signature: false,
      sousTotal: false,
      note: false,
      selected_columns: [],
      selected_totals: [],
      template: null,
      columns: [
        { value: 'quantity', text: 'Quantité' },
        { value: 'unitPrice', text: 'Matériau – Coût unitaire' },
        { value: 'materialCost', text: 'Matériau – Coût total' },
        { value: 'subcontracting', text: 'Sous-traitance' },
        { value: 'laborHourlyRate', text: "Main d'oeuvre – Constante" },
        { value: 'estimatedLaborTime', text: "Main d'oeuvre – Temps" },
        { value: 'laborCost', text: "Main d'oeuvre – Coût" },
        { value: 'total', text: 'Coût total' },
      ],
      totals: [
        { value: 'project', text: 'Projet' },
        { value: 'phases', text: 'Phases' },
        { value: 'components', text: 'Assemblages' },
      ],
      predefinedReports: [
        {
          title: 'Devis du client',
          value: {
            type: 'client',
            columns: 'all',
            totals: 'all',
            signature: true,
            sousTotal: true,
          },
        },
        {
          title: 'Liste de matériaux - Client',
          value: {
            type: 'client',
            columns: ['quantity'],
            totals: ['none'],
            only: 'materials',
            signature: false,
            sousTotal: false,
          },
        },
        {
          title: 'Liste de matériaux - Entrepreneur',
          value: {
            type: 'contractor',
            columns: ['quantity', 'unitPrice', 'materialCost'],
            totals: 'all',
            only: 'materials',
            signature: false,
            sousTotal: true,
          },
        },
        {
          title: 'Liste de sous-traitants - Client',
          value: {
            type: 'client',
            columns: ['subcontracting'],
            totals: ['project'],
            only: 'subcontracts',
            signature: true,
            sousTotal: true,
          },
        },
        {
          title: 'Liste de sous-traitants - Entrepreneur',
          value: {
            type: 'contractor',
            columns: ['subcontracting'],
            totals: 'all',
            only: 'subcontracts',
            signature: true,
            sousTotal: true,
          },
        },
        {
          title: 'Sommaire client',
          value: {
            type: 'client',
            columns: ['materialCost', 'subcontracting', 'laborCost', 'total'],
            totals: ['phases', 'project'],
            signature: true,
            sousTotal: true,
          },
        },
      ],
    };
  },
  computed: {
    modal() {
      return this.$store.getters.isReportModal;
    },
    job() {
      return this.$store.getters.jobId;
    },
  },
  methods: {
    openReports(id) {
      if (this.$route.params.id != null) {
        id = this.$route.params.id;
      }

      let columns = this.selected_columns.map(s => s.value.toString());
      let totals = this.selected_totals.map(s => s.value.toString());

      if (columns.length === 0) {
        columns = ['none'];
      }
      if (totals.length === 0) {
        totals = ['none'];
      }

      this.$router.replace({
        name: 'Reports',
        params: { id: id },
        query: {
          type: String(this.type),
          showComponents: String(this.showComponents),
          showMaterials: String(this.showMaterials),
          columns: columns,
          totals: totals,
          only: this.only ? String(this.only) : null,
          signature: String(this.signature),
          subtotal: String(this.sousTotal),
          note: String(this.note),
        },
      });
      this.reset();
      this.$store.dispatch('toggle');
    },
    cancel() {
      this.$store.dispatch('toggle');
      this.reset();
    },
    loadTemplate(event) {
      if (!event) {
        return;
      }

      this.reset();
      this.type = event.type;
      this.showComponents = event.showComponents;
      this.showMaterials = event.showMaterials;
      this.signature = event.signature;
      this.sousTotal = event.sousTotal;
      this.note = event.note;
      this.only = event.only;
      this.selectAllColumns(event.columns);
      this.selectAllTotals(event.totals);

      this.$nextTick(() => {
        this.template = null;
        this.$refs['templateSelect'].reset();
      });
    },
    toggleColumn(column) {
      if (!this.selected_columns.includes(column)) {
        this.selected_columns.push(column);
        const columnIndex = this.columns.indexOf(column);
        this.columns[columnIndex].selectedNew = true;
      } else {
        this.selected_columns = this.selected_columns.filter(c => c != column);
        const columnIndex = this.columns.indexOf(column);
        this.columns[columnIndex].selectedNew = false;
      }
    },
    toggleTotal(total) {
      if (!this.selected_totals.includes(total)) {
        this.selected_totals.push(total);
        const columnIndex = this.totals.indexOf(total);
        this.totals[columnIndex].selectedNew = true;
      } else {
        this.selected_totals = this.selected_totals.filter(t => t != total);
        const columnIndex = this.totals.indexOf(total);
        this.totals[columnIndex].selectedNew = false;
      }
    },
    selectAllColumns(columns = null) {
      columns = columns === 'all' ? null : columns;
      this.columns = this.columns.map(x => ({
        ...x,
        selected: false,
        selectedNew: columns ? columns.includes(x.value) : true,
      }));
      this.selected_columns = this.columns.filter(x => x.selectedNew);
    },
    resetColumns() {
      this.selected_columns = [];
      this.columns = this.columns.map(x => ({
        ...x,
        selected: false,
        selectedNew: false,
      }));
    },
    selectAllTotals(totals = null) {
      totals = totals === 'all' ? null : totals;
      this.totals = this.totals.map(x => ({
        ...x,
        selected: false,
        selectedNew: totals ? totals.includes(x.value) : true,
      }));
      this.selected_totals = this.totals.filter(x => x.selectedNew);
    },
    resetTotals() {
      this.selected_totals = [];
      this.totals = this.totals.map(x => ({
        ...x,
        selected: false,
        selectedNew: false,
      }));
    },
    informationRipple() {
      if (!this.showComponents) {
        this.showMaterials = false;
      }
    },
    reset() {
      this.resetTotals();
      this.resetColumns();
      this.type = 'contractor';
      this.showComponents = true;
      this.showMaterials = true;
      this.only = null;
      this.signature = false;
      this.sousTotal = false;
      this.note = false;
    },
  },
};
</script>

<style lang="stylus" scoped>
:deep(.v-checkbox .v-selection-control) {
  margin-inline-start: calc((24px - var(--v-selection-control-size)) / 2);
}

:deep(.v-checkbox .v-label),
:deep(.v-radio .v-label) {
  color: #757575;
  opacity: 1;
}

.form-label {
  display: flex;
  flex-direction: row;
  width: 100%;

  label {
    padding-top: 2px;
    margin-right: 10px;
    color: rgba(0, 0, 0, 0.87);
    font-size: 1rem;
    font-weight: bold;
  }
}

.selected-options {
  padding: 0 !important;
}

.selected-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.select-all-container {
  height: 100%;
  padding: 2px;
  div {
    padding: 2px 10px;
    background-color: rgba(249, 128, 47, 0.4);
    cursor: pointer;
    border-radius: 10px;
  }
}

.column-name-container{
  width:100%;
  height: 100%;
  .column-name-block{
    padding:2px;
    height: 100%;
    display:block;

    .column-name-text{
      background-color: #E6E6E6;
      height :100%;
      display:flex;
      padding:10px;
      align-items:center;
    }
    .selected {
      background-color: #959595;
    }
    .selected-new {
      background-color: #f9802f;
    }
  }
}
</style>
